// sound.ts — WebAudio SFX synthesis for the play surface (P2 design §4). No external audio files:
// every effect is synthesized from oscillators + filtered white noise. The file deliberately holds
// two halves behind one export surface:
//   純粋部 (pure): SfxLayer/SfxRecipe types, the RECIPES table (§4.2 確定値) and `scheduleOf` —
//     AudioContext-independent so vitest can pin the numbers in a node environment.
//   実体部 (engine): AudioEngine + the `sfx` module singleton — lazy AudioContext, mobile autoplay
//     unlock, one-shot node graphs. No persistence here (D10): shell/integrate hydrates volume.
// sound.ts must not import any other studio file (§4.4 並列安全).

export type Wave = 'sine' | 'triangle' | 'square' | 'sawtooth' | 'noise';

export interface SfxLayer {
  wave: Wave;
  freq?: number;
  freqEnd?: number; // Hz。freqEnd 指定時 exponentialRamp（noise は無視）
  filter?: { type: 'bandpass' | 'lowpass' | 'highpass'; freq: number; freqEnd?: number; q?: number };
  delay?: number; // s（先頭からのオフセット）
  attack: number;
  decay: number;
  peak: number; // s / s / 0..1
}

export interface SfxRecipe {
  layers: SfxLayer[];
  jitterCents?: number;
}

export type SfxName =
  | 'discard'
  | 'discardStrong'
  | 'draw'
  | 'riichi'
  | 'callPon'
  | 'callChii'
  | 'callKan'
  | 'winFanfare'
  | 'yakumanFanfare'
  | 'tick'
  | 'uiClick'
  | 'doraFlip'
  | 'scoreMove'
  | 'deal';

// ---------------------------------------------------------------------------
// レシピ表（§4.2 確定値）— helpers keep the repeated shapes literal, numbers as-is.
// ---------------------------------------------------------------------------

// discard の「カチッ」: ±15% の周波数ジッタ ≈ ±242 cents (1200·log2(1.15) ≈ 241.96)。
const DISCARD_JITTER_CENTS = 242;

// discard ①+② — discardStrong reuses them with ① peak boosted to 1.0.
function discardLayers(clickPeak: number): SfxLayer[] {
  return [
    { wave: 'noise', filter: { type: 'bandpass', freq: 2200, q: 1.2 }, attack: 0.001, decay: 0.07, peak: clickPeak },
    { wave: 'sine', freq: 180, freqEnd: 90, attack: 0.001, decay: 0.06, peak: 0.5 },
  ];
}

// 鳴き声ハーモニー共通形: square 高+低 2 層, lowpass 900Hz Q=1, atk .003, dec .15。
function callToneLayers(hi: number, lo: number): SfxLayer[] {
  return [
    { wave: 'square', freq: hi, filter: { type: 'lowpass', freq: 900, q: 1 }, attack: 0.003, decay: 0.15, peak: 0.4 },
    { wave: 'square', freq: lo, filter: { type: 'lowpass', freq: 900, q: 1 }, attack: 0.003, decay: 0.15, peak: 0.3 },
  ];
}

// winFanfare: ペンタトニック上行 6 音、各音 triangle+sine 二重。終音のみ dec .9。
const WIN_PENTA_UP = [523.25, 587.33, 659.25, 783.99, 880, 1046.5];
function winFanfareLayers(): SfxLayer[] {
  const layers: SfxLayer[] = [];
  WIN_PENTA_UP.forEach((freq, i) => {
    const decay = i === WIN_PENTA_UP.length - 1 ? 0.9 : 0.5;
    const delay = i * 0.07;
    layers.push({ wave: 'triangle', freq, filter: { type: 'lowpass', freq: 4000 }, delay, attack: 0.005, decay, peak: 0.25 });
    layers.push({ wave: 'sine', freq, filter: { type: 'lowpass', freq: 4000 }, delay, attack: 0.005, decay, peak: 0.15 });
  });
  // 終端のシャラッ (noise highpass 6kHz)。
  layers.push({ wave: 'noise', filter: { type: 'highpass', freq: 6000 }, delay: 0.35, attack: 0.005, decay: 0.8, peak: 0.12 });
  return layers;
}

// yakumanFanfare ②: アルペジオ C4→C6 8 音（端点と音数のみ設計確定 — メジャー6thで補間）。
const YAKUMAN_ARP = [261.63, 329.63, 392.0, 440.0, 523.25, 659.25, 783.99, 1046.5];
// yakumanFanfare ③: 持続和音 C5+E5+G5+C6。
const YAKUMAN_CHORD = [523.25, 659.25, 783.99, 1046.5];
function yakumanFanfareLayers(): SfxLayer[] {
  const layers: SfxLayer[] = [
    // ①銅鑼 — 3 partials, 長い減衰。
    { wave: 'sine', freq: 98, attack: 0.005, decay: 2.5, peak: 0.5 },
    { wave: 'sine', freq: 147, attack: 0.005, decay: 2.5, peak: 0.3 },
    { wave: 'sine', freq: 196, attack: 0.005, decay: 2.5, peak: 0.2 },
  ];
  YAKUMAN_ARP.forEach((freq, i) => {
    layers.push({
      wave: 'sawtooth', freq, filter: { type: 'lowpass', freq: 2500, q: 3 },
      delay: 0.5 + i * 0.09, attack: 0.005, decay: 0.6, peak: 0.3,
    });
  });
  for (const freq of YAKUMAN_CHORD) {
    layers.push({ wave: 'sine', freq, delay: 1.3, attack: 0.3, decay: 1.4, peak: 0.2 });
  }
  return layers;
}

export const RECIPES: Record<SfxName, SfxRecipe> = {
  // 打牌 ~90ms: noise クリック + 低域サムプ 180→90Hz。
  discard: { layers: discardLayers(0.9), jitterCents: DISCARD_JITTER_CENTS },
  // 立直打牌 ~110ms: discard(①peak→1.0) + square 80Hz の腹。
  discardStrong: {
    layers: [...discardLayers(1.0), { wave: 'square', freq: 80, attack: 0.001, decay: 0.09, peak: 0.4 }],
    jitterCents: DISCARD_JITTER_CENTS,
  },
  // 自摸 ~45ms。
  draw: {
    layers: [
      { wave: 'triangle', freq: 700, attack: 0.002, decay: 0.04, peak: 0.3 },
      { wave: 'noise', filter: { type: 'highpass', freq: 3000 }, attack: 0.001, decay: 0.03, peak: 0.15 },
    ],
  },
  // 立直 ~600ms: saw 二音 (A4→E5, lowpass sweep 1200→3500Hz) + noise sweep + 低域 60Hz。
  riichi: {
    layers: [
      { wave: 'sawtooth', freq: 440, filter: { type: 'lowpass', freq: 1200, freqEnd: 3500 }, attack: 0.005, decay: 0.18, peak: 0.35 },
      { wave: 'sawtooth', freq: 659.25, filter: { type: 'lowpass', freq: 1200, freqEnd: 3500 }, delay: 0.12, attack: 0.005, decay: 0.18, peak: 0.35 },
      { wave: 'noise', filter: { type: 'bandpass', freq: 500, freqEnd: 4000 }, attack: 0.001, decay: 0.4, peak: 0.2 },
      { wave: 'sine', freq: 60, attack: 0.001, decay: 0.25, peak: 0.5 },
    ],
  },
  // 鳴き三種 ~200/250ms — 同形でルートだけ変える。
  callPon: { layers: callToneLayers(330, 165) },
  callChii: { layers: callToneLayers(392, 196) },
  callKan: {
    layers: [...callToneLayers(262, 131), { wave: 'sine', freq: 65, attack: 0.001, decay: 0.2, peak: 0.45 }],
  },
  // 和了 ~1.2s / 役満 ~2.8s。
  winFanfare: { layers: winFanfareLayers() },
  yakumanFanfare: { layers: yakumanFanfareLayers() },
  // 残時間 tick 25ms。step ごとの音程上昇 (1000+step×40Hz) は呼び出し側が detuneCents で付ける
  // (40Hz@1kHz ≈ 69 cents/step) — RECIPES は静的表なので基準 1000Hz のみ持つ。
  tick: {
    layers: [{ wave: 'square', freq: 1000, filter: { type: 'highpass', freq: 600 }, attack: 0.001, decay: 0.025, peak: 0.15 }],
  },
  // UI クリック 20ms。
  uiClick: {
    layers: [
      { wave: 'sine', freq: 900, attack: 0.001, decay: 0.02, peak: 0.12 },
      { wave: 'noise', filter: { type: 'highpass', freq: 5000 }, attack: 0.001, decay: 0.012, peak: 0.08 },
    ],
  },
  // ドラ表示めくり ~300ms: G6+C7 きらめき + 高域ノイズ。
  doraFlip: {
    layers: [
      { wave: 'triangle', freq: 1568, attack: 0.002, decay: 0.3, peak: 0.2 },
      { wave: 'triangle', freq: 2093, attack: 0.002, decay: 0.3, peak: 0.12 },
      { wave: 'noise', filter: { type: 'highpass', freq: 8000 }, attack: 0.001, decay: 0.25, peak: 0.15 },
    ],
  },
  // 点棒移動 ~160ms: E6 → A6 の二連音。
  scoreMove: {
    layers: [
      { wave: 'sine', freq: 1318.5, attack: 0.001, decay: 0.08, peak: 0.2 },
      { wave: 'sine', freq: 1760, delay: 0.06, attack: 0.001, decay: 0.1, peak: 0.2 },
    ],
  },
  // 配牌ザラッ ~320ms: noise lowpass sweep 1800→600Hz。
  deal: {
    layers: [{ wave: 'noise', filter: { type: 'lowpass', freq: 1800, freqEnd: 600 }, attack: 0.02, decay: 0.3, peak: 0.25 }],
  },
};

// ---------------------------------------------------------------------------
// 純粋部 — scheduleOf。AudioContext 非依存・決定論（rng 未注入なら jitter ゼロ）。
// ---------------------------------------------------------------------------

export interface ScheduledLayer {
  wave: Wave;
  /** s — play start offset (= recipe delay). */
  startAt: number;
  /** s — startAt + attack + decay（エンベロープ完全終了点）. */
  endAt: number;
  attack: number;
  decay: number;
  peak: number;
  /** Hz、jitter 適用済み。noise レイヤはオシレータを持たないので省略（§4.1）。 */
  freq?: number;
  freqEnd?: number;
  filter?: { type: 'bandpass' | 'lowpass' | 'highpass'; freq: number; freqEnd?: number; q?: number };
}

/**
 * 純粋・AudioContext 非依存 → vitest 可。jitter の乱数は rng 注入時のみ使用 — 既定（rng なし）は
 * 偏差 0 の決定論スケジュール。実体部 (AudioEngine.play) だけが Math.random を渡す。
 */
export function scheduleOf(r: SfxRecipe, rng?: () => number): { tEnd: number; layers: ScheduledLayer[] } {
  // 1 再生につき 1 回だけ乱数を引き、全レイヤへ同じ比率を掛ける —
  // 和音・フィルタの相対チューニングを保ったまま全体のピッチが揺れる。
  const cents = r.jitterCents !== undefined && rng ? (rng() * 2 - 1) * r.jitterCents : 0;
  const ratio = Math.pow(2, cents / 1200);
  let tEnd = 0;
  const layers = r.layers.map((l): ScheduledLayer => {
    const startAt = l.delay ?? 0;
    const endAt = startAt + l.attack + l.decay;
    if (endAt > tEnd) tEnd = endAt;
    const out: ScheduledLayer = { wave: l.wave, startAt, endAt, attack: l.attack, decay: l.decay, peak: l.peak };
    if (l.wave !== 'noise') {
      // noise はオシレータ freq を無視（§4.1）。exponentialRamp 前提なので端点は常に正。
      if (l.freq !== undefined) out.freq = l.freq * ratio;
      if (l.freqEnd !== undefined) out.freqEnd = l.freqEnd * ratio;
    }
    if (l.filter) {
      const f: NonNullable<ScheduledLayer['filter']> = { type: l.filter.type, freq: l.filter.freq * ratio };
      if (l.filter.freqEnd !== undefined) f.freqEnd = l.filter.freqEnd * ratio;
      if (l.filter.q !== undefined) f.q = l.filter.q;
      out.filter = f;
    }
    return out;
  });
  return { tEnd, layers };
}

// ---------------------------------------------------------------------------
// 実体部 — AudioEngine。React 非依存・unmount 無関係（per-component ノードなし）。
// ---------------------------------------------------------------------------

export interface PlayOptions {
  delayMs?: number;
  gainScale?: number;
  detuneCents?: number;
}

export class AudioEngine {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private noiseBuf: AudioBuffer | null = null; // 白噪 2s — lazy 生成・全再生で使い回し
  private volume = 1; // 0..1（ユーザー値。実ゲインは v*v の聴感補正）
  private muted = false;
  private ducked = false; // タブ非表示 / blur 中 → 0.1s ランプで消音（§11.E）

  constructor() {
    // vitest(node) / SSR 安全弁: DOM が無ければリスナーも ctx も作らず、play() は常に no-op。
    if (typeof window === 'undefined' || typeof document === 'undefined') return;
    // モバイル autoplay unlock: 最初のユーザージェスチャで ctx 生成 + 同一ジェスチャ内 resume()。
    // unlock 前の play() は黙って捨てる（キューしない、§4.3）。
    const unlock = (): void => this.ensure();
    window.addEventListener('pointerdown', unlock, { once: true, capture: true });
    window.addEventListener('keydown', unlock, { once: true, capture: true });
    document.addEventListener('visibilitychange', () => {
      this.setDucked(document.visibilityState !== 'visible');
    });
    window.addEventListener('blur', () => this.setDucked(true));
    window.addEventListener('focus', () => this.setDucked(false));
  }

  /** 0..1。master.gain = v*v（聴感補正）。永続化はしない (D10) — hydrate は shell/integrate 側。 */
  setVolume(v: number): void {
    this.volume = Math.min(1, Math.max(0, v));
    this.applyGain(0.03);
  }

  setMuted(b: boolean): void {
    this.muted = b;
    this.applyGain(0.03);
  }

  play(name: SfxName, opts: PlayOptions = {}): void {
    const ctx = this.ctx;
    const master = this.master;
    if (!ctx || !master) return; // unlock 前 → 捨てる
    if (this.muted) return; // 消音中はノードを作るだけ無駄
    // jitter の乱数は実体部だけが注入する（scheduleOf 自体は決定論のまま）。
    const sched = scheduleOf(RECIPES[name], Math.random);
    const t0 = ctx.currentTime + Math.max(0, opts.delayMs ?? 0) / 1000;
    const detuneRatio = Math.pow(2, (opts.detuneCents ?? 0) / 1200);
    const gainScale = opts.gainScale ?? 1;
    for (const layer of sched.layers) this.spawnLayer(ctx, master, layer, t0, detuneRatio, gainScale);
  }

  // --- internals ---

  private ensure(): void {
    if (this.ctx) {
      if (this.ctx.state === 'suspended') void this.ctx.resume();
      return;
    }
    // Safari 旧系は webkitAudioContext のみ。lib.dom の Window 型はコンストラクタを載せないので cast。
    const w = window as unknown as { AudioContext?: typeof AudioContext; webkitAudioContext?: typeof AudioContext };
    const AC = w.AudioContext ?? w.webkitAudioContext;
    if (!AC) return;
    const ctx = new AC();
    // master Gain → DynamicsCompressor(threshold -18dB, ratio 4) → destination。
    // コンプレッサは重ね鳴り（連続打牌 + ファンファーレ等）のクリップ防止。
    const master = ctx.createGain();
    const comp = ctx.createDynamicsCompressor();
    comp.threshold.value = -18;
    comp.ratio.value = 4;
    master.connect(comp);
    comp.connect(ctx.destination);
    master.gain.value = this.effectiveGain();
    this.ctx = ctx;
    this.master = master;
    if (ctx.state === 'suspended') void ctx.resume(); // 同一ジェスチャ内で resume
  }

  private effectiveGain(): number {
    return this.muted || this.ducked ? 0 : this.volume * this.volume;
  }

  private setDucked(b: boolean): void {
    if (this.ducked === b) return;
    this.ducked = b;
    this.applyGain(0.1); // §4.3: 0.1s ランプで消音 / 復帰
  }

  private applyGain(rampSec: number): void {
    if (!this.ctx || !this.master) return;
    const g = this.master.gain;
    const now = this.ctx.currentTime;
    g.cancelScheduledValues(now);
    g.setValueAtTime(g.value, now);
    g.linearRampToValueAtTime(this.effectiveGain(), now + rampSec);
  }

  private noiseBuffer(ctx: AudioContext): AudioBuffer {
    if (!this.noiseBuf) {
      const len = Math.floor(ctx.sampleRate * 2);
      const buf = ctx.createBuffer(1, len, ctx.sampleRate);
      const ch = buf.getChannelData(0);
      for (let i = 0; i < len; i++) ch[i] = Math.random() * 2 - 1;
      this.noiseBuf = buf;
    }
    return this.noiseBuf;
  }

  /** ワンショットのノード鎖 source→(filter)→envelope→master を組み、onended で disconnect。 */
  private spawnLayer(
    ctx: AudioContext,
    out: GainNode,
    l: ScheduledLayer,
    t0: number,
    detuneRatio: number,
    gainScale: number,
  ): void {
    const peak = l.peak * gainScale;
    if (peak <= 0) return; // exponentialRamp は 0 を扱えない & 無音は鳴らす意味がない
    const start = t0 + l.startAt;
    const end = t0 + l.endAt;
    const nodes: AudioNode[] = [];

    let src: AudioScheduledSourceNode;
    if (l.wave === 'noise') {
      const s = ctx.createBufferSource();
      s.buffer = this.noiseBuffer(ctx);
      s.loop = true; // 共有 2s バッファ — loop で長い尾でも切れない
      src = s;
    } else {
      const o = ctx.createOscillator();
      o.type = l.wave;
      o.frequency.setValueAtTime((l.freq ?? 440) * detuneRatio, start);
      if (l.freqEnd !== undefined) o.frequency.exponentialRampToValueAtTime(l.freqEnd * detuneRatio, end);
      src = o;
    }
    nodes.push(src);

    let tail: AudioNode = src;
    if (l.filter) {
      const biq = ctx.createBiquadFilter();
      biq.type = l.filter.type;
      biq.frequency.setValueAtTime(l.filter.freq * detuneRatio, start);
      if (l.filter.freqEnd !== undefined) biq.frequency.exponentialRampToValueAtTime(l.filter.freqEnd * detuneRatio, end);
      if (l.filter.q !== undefined) biq.Q.value = l.filter.q;
      tail.connect(biq);
      nodes.push(biq);
      tail = biq;
    }

    // エンベロープ: linear attack → exponential decay（自然減衰）→ 終端で完全 0。
    const env = ctx.createGain();
    env.gain.setValueAtTime(0, start);
    env.gain.linearRampToValueAtTime(peak, start + l.attack);
    env.gain.exponentialRampToValueAtTime(0.0008, end);
    env.gain.setValueAtTime(0, end);
    tail.connect(env);
    env.connect(out);
    nodes.push(env);

    src.start(start);
    src.stop(end + 0.02);
    src.onended = () => {
      for (const n of nodes) n.disconnect();
    };
  }
}

/** モジュールシングルトン。設定の hydrate は shell/integrate が sfx.setVolume(...) で行う。 */
export const sfx = new AudioEngine();
